import classNames from 'classnames';
import { useState } from 'react';

import FormDisclaimer from '~components/FormDisclaimer/FormDisclaimer';
import EmailLeadForm from '~components/LeadForm/EmailLeadForm/EmailLeadForm';
import { useAccessibleTextColor } from '~globals/common';
import successIcon from '~images/common/circle-green-checkmark.svg';

type Props = {
    imageurl?: string;
    heading?: string;
    description?: string;
    ctacopy?: string;
    backgroundsurface?: SupportedBackgroundColor;
};

const GetMSGuide = ({
    imageurl,
    heading = 'Get your free Medicare Supplement Insurance Guide',
    description = "It's everything you'll want to know about Medicare Supplement insurance in a document you can print or save.",
    ctacopy = 'Get the Guide',
    backgroundsurface = 'bg-surface-light',
}: Props) => {
    const [isSucceeded, setIsSucceeded] = useState(false);

    return (
        <section className={classNames('px-4 py-10 md:px-6 lg:py-16', backgroundsurface)}>
            <div className="flex flex-col flex-col-reverse md:flex-row md:justify-center md:gap-6 max-w-1200 mx-auto">
                <div className={classNames('my-8 md:mt-0 md:mb-4 lg:mb-8', 'lg:max-w-[580px]')}>
                    <h2
                        className={classNames(
                            useAccessibleTextColor(backgroundsurface, 'text-link-visited'),
                            'font-poppins font-bold',
                            'text-center md:text-start',
                            'text-24 leading-32 max-w-[275px] mx-auto md:max-w-full md:text-40 md:leading-56'
                        )}
                    >
                        {heading}
                    </h2>
                    <p
                        className={classNames(
                            useAccessibleTextColor(backgroundsurface),
                            'font-poppins font-semibold mt-3',
                            'text-18 leading-28 text-center',
                            'md:text-24 md:leading-32 md:text-start lg:text-start'
                        )}
                    >
                        {description}
                    </p>

                    {!isSucceeded ? (
                        <EmailLeadForm
                            uid="ms-get-guide"
                            mktoFormId={1113}
                            emailLabelText="Email"
                            buttonText={ctacopy}
                            leadInfo={{ advertiseSource: 'MSNavLP' }}
                            qaInput="ms-get-guide-textbox"
                            qaButton="ms-get-guide-submit"
                            beforeSubmit={() => setIsSucceeded(false)}
                            onSuccess={() => setIsSucceeded(true)}
                            leadTrackingAction={{ page: { action: 'MS: get the navigator' } }}
                            ariaLabel="Enter your email to get medicare supplement insurance guide in your inbox."
                        />
                    ) : (
                        <div
                            className="flex items-start bg-green-lightest shadow mt-5 px-2 py-4 md:p-5"
                            data-qa="blog-email-success"
                        >
                            <img alt="" src={successIcon} width="30" height="45" className="mr-3" />
                            <p>
                                <span className="block font-bold text-xl md:text-2xl">
                                    Success!
                                </span>
                                <span className="block text-base">
                                    Your Medicare Supplement Guide has been sent!
                                </span>
                            </p>
                        </div>
                    )}

                    <FormDisclaimer
                        customDisclaimer="You consent to receive marketing emails from eHealth. Your information and use of this site is governed by our most recent "
                        className="text-16 lg:text-16 leading-24 font-sans lg:leading-16 mt-6"
                    />
                </div>
                <div className="max-h-64 md:max-h-full overflow-hidden">
                    <img src={imageurl} alt="" className="w-64 mx-auto md:w-auto" />
                </div>
            </div>
        </section>
    );
};

export default GetMSGuide;
